import React from 'react';
import { BreadcrumbEllipsis } from '../BreadcrumbEllipsis';
import { BreadcrumbItem } from '../BreadcrumbItem';
import { BreadcrumbLink } from '../BreadcrumbLink';
import { BreadcrumbList } from '../BreadcrumbList';
import { BreadcrumbPage } from '../BreadcrumbPage';
import { BreadcrumbSeparator } from '../BreadcrumbSeparator';
import styles from '../index.module.scss';

type Item = {
  /** 表示テキスト */
  label: string;
  /** リンク先 */
  href?: string;
};
type Props = {
  /**
   * 表示する項目の配列
   */
  items: Item[];
  'aria-label'?: string;
};

export const Breadcrumb: React.FC<Props> = ({ items, ...props }) => {
  const ariaLabel = props['aria-label'] ?? 'breadcrumb';

  /**
   * 要素が6つ以上の場合は省略して一部を表示する
   * 表示するのは先頭>...>最後から3番目・2番目・現在地
   */
  const shouldEllipsize = items.length >= 6;
  if (shouldEllipsize) {
    const head = items[0];
    const tail = items.slice(-3);

    return (
      <nav className={styles.breadcrumb} aria-label={ariaLabel}>
        <BreadcrumbList>
          <BreadcrumbItem key='head-link'>
            {head.href ? (
              <BreadcrumbLink href={head.href}>{head.label}</BreadcrumbLink>
            ) : (
              <BreadcrumbPage>{head.label}</BreadcrumbPage>
            )}
          </BreadcrumbItem>
          <BreadcrumbItem key='head-separator'>
            <BreadcrumbSeparator />
          </BreadcrumbItem>

          {/* 省略 */}
          <BreadcrumbItem key='ellipsis'>
            <BreadcrumbEllipsis />
          </BreadcrumbItem>
          <BreadcrumbItem key='ellipsis-separator'>
            <BreadcrumbSeparator />
          </BreadcrumbItem>

          {/* 末尾3つ（最後は現在地） */}
          {tail.map(({ label, href }, i) => {
            const isLast = i === tail.length - 1;
            return isLast ? (
              <BreadcrumbItem key={i}>
                <BreadcrumbPage>{label}</BreadcrumbPage>
              </BreadcrumbItem>
            ) : href ? (
              <React.Fragment key={i}>
                <BreadcrumbItem>
                  <BreadcrumbLink href={href}>{label}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <BreadcrumbSeparator />
                </BreadcrumbItem>
              </React.Fragment>
            ) : (
              <React.Fragment key={i}>
                <BreadcrumbItem>
                  <BreadcrumbPage>{label}</BreadcrumbPage>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <BreadcrumbSeparator />
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </nav>
    );
  }

  /**
   * 全ての項目を表示
   */
  return (
    <nav className={styles.breadcrumb} aria-label={ariaLabel}>
      <BreadcrumbList>
        {items.map(({ label, href }, i) => {
          const isLast = items.length === i + 1;
          return isLast ? (
            <BreadcrumbItem key={i}>
              <BreadcrumbPage>{label}</BreadcrumbPage>
            </BreadcrumbItem>
          ) : href ? (
            <React.Fragment key={i}>
              <BreadcrumbItem>
                <BreadcrumbLink href={href}>{label}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbSeparator />
              </BreadcrumbItem>
            </React.Fragment>
          ) : (
            <React.Fragment key={i}>
              <BreadcrumbItem>
                <BreadcrumbPage>{label}</BreadcrumbPage>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbSeparator />
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </nav>
  );
};
